/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
    Form as FormikForm,
    TextField,
    DatePickerField,
    AutocompleteField,
    Masks,
    DateTimePickerField,
    TextareaField,
} from "components/common_components/Formik";
import { Field } from "formik";
import * as Yup from "yup";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import EditIcon from "@material-ui/icons/Edit";
import BackspaceIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { DataSearchDialog } from "components/common_components/Dialogs";
import Divider from "@material-ui/core/Divider";
import FileCaseEntity from "entities/InjuryPersonalInfo";
import * as EntityUtils from "entities/utils";
import ModulesContext from "../Module/ModulesContext";
import ParentInfoControl from "./ParentInfoControl";
import { getDateMillis } from "utils/dateUtils";
import ClientInfo from "./ClientInfo";

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: "unset",
        paddingTop: theme.spacing(3),
        "& div.MuiFormControl-marginDense": {
            marginTop: 0,
            marginBottom: 0,
        },
    },
    buttons: {
        marginTop: theme.spacing(2),
    },
    button: {
        margin: theme.spacing(0, 1, 0, 0),
    },
}));

function Form({ fileCase, resources, mode, onEdit, prefix, consecutive, onSubmit, ...rest }) {
    const classes = useStyles();
    const history = useHistory();
    const readOnly = mode === "detail";
    const { countries, types, status, fileLocations } = resources;
    const [initialValues, setInitialValues] = useState(undefined);

    const { Customers, getClientPhotoAsync, permissions } = useContext(ModulesContext);

    function init() {
        if (mode === "add") {
            setInitialValues({
                ...EntityUtils.mapEntity(FileCaseEntity, fileCase),
                prefix,
                consecutive: Number(consecutive),
            });
        } else {
            setInitialValues({ ...EntityUtils.mapEntity(FileCaseEntity, fileCase) });
        }
    }

    useEffect(() => {
        init();
    }, [fileCase]);

    const validationSchema = Yup.object({
        prefix: Yup.string().required("Required field").max(5),
        consecutive: Yup.number().min(1).required("Required field"),
        notes: Yup.string().notRequired().nullable().max(500),
        other: Yup.string().notRequired().nullable().max(200),
        insuranceNo: Yup.string().notRequired().nullable().max(50),
        insuranceCo: Yup.string().notRequired().nullable().max(100),
        dlNo: Yup.string().notRequired().nullable().max(50),
        policyNo: Yup.string().notRequired().nullable().max(50),
        insurerNo: Yup.string().notRequired().nullable().max(50),
        placeOfAccident: Yup.string().notRequired().nullable().max(100),
        parentId: Yup.number().notRequired().nullable(),
    });

    function filterClientsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  homePhone: filterValue,
                  ssn: filterValue,
              }
            : {};
    }

    const [searchClientDialogProps, setSearchClientDialogProps] = useState({
        open: false,
        title: "Select a client",
        onClose: () => setSearchClientDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "asc", orderBy: "firstName" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: Customers.getListAsync,
            filterBuilder: filterClientsBuilder,
        },
    });

    function setCustomer(selectedItems, { values: currentValues, setValues, setFieldValue }) {
        const client = selectedItems[0];
        if (client) {
            getClientPhotoAsync(client.id);
            setValues({
                ...currentValues,
                notes: client.notes,
                client: { ...client, country: client.country || null, birthDate: client.birthDate || null },
            });
        }
    }

    function goToCustomer(customerId) {
        history.push(`/customers/${customerId}`);
    }

    function searchCustomer(form) {
        const headCells = [
            { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
            { index: 2, id: "lastName", disablePadding: false, label: "Last Name" },
            { index: 3, id: "homePhone", disablePadding: true, label: "Home Phone" },
            { index: 4, id: "birthDateString", disablePadding: true, label: "Date of Birth" },
            { index: 5, id: "ssn", disablePadding: true, label: "SSN" },
        ];
        setSearchClientDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCustomer(selectedItems, form),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    function onSubmitHandler(values, form) {
        const { startDate, dateOfAccident, ...payload } = values;
        const startDateInMillis = getDateMillis(startDate);
        const dateOfAccidentInMillis = getDateMillis(dateOfAccident);

        onSubmit(
            {
                ...payload,
                startDate: startDateInMillis,
                dateOfAccident: dateOfAccidentInMillis,
            },
            form,
        );
    }

    return (
        <>
            {initialValues && (
                <FormikForm
                    validateOnChange={false}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    showButtons={!readOnly}
                    onSubmit={onSubmitHandler}
                    {...rest}
                >
                    <Grid container spacing={3}>
                        <Grid item md={12} lg={7}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <Grid container direction="row" spacing={1}>
                                    <Grid item xs={12} md={2}>
                                        <TextField disabled fieldName="prefix" uppercase margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            disabled={mode !== "add"}
                                            fieldName="consecutive"
                                            label="File No *"
                                            uppercase
                                            margin="dense"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                                            <Grid item>
                                                <Field>
                                                    {({ _, form }) => (
                                                        <>
                                                            {mode === "add" && (
                                                                <Button
                                                                    variant="text"
                                                                    size="small"
                                                                    color="primary"
                                                                    startIcon={<SearchIcon />}
                                                                    onClick={() => searchCustomer(form)}
                                                                >
                                                                    Client
                                                                </Button>
                                                            )}
                                                            {form.values.client.id && (
                                                                <Button
                                                                    variant="text"
                                                                    size="small"
                                                                    color="secondary"
                                                                    startIcon={<EditIcon />}
                                                                    onClick={() => goToCustomer(form.values.client.id)}
                                                                >
                                                                    Go to Client
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {fileLocations && (
                                            <AutocompleteField
                                                disabled={readOnly}
                                                fieldName="fileLocation"
                                                label="File Location"
                                                options={fileLocations}
                                                valueKey="code"
                                                labelKey="name"
                                                margin="dense"
                                                getOptionLabel={({ name }) => name}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider light />
                                    </Grid>
                                    <ParentInfoControl mode={mode} />
                                    <Grid item xs={12}>
                                        <Divider light />
                                    </Grid>
                                    <ClientInfo />
                                    <Grid item xs={12}>
                                        <Divider light />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="other" label="Other" margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="insuranceNo" label="Insurance No." margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="insuranceCo" label="Insurance Co." margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="dlNo" label="DL No." margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="policyNo" label="Policy No." margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled={readOnly} fieldName="insurerNo" label="Insurer No." margin="dense" />
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Grid>
                        <Grid item md={12} lg={5}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <Grid container direction="row" spacing={2}>
                                    <Grid item xs={12} md={5}>
                                        <DatePickerField disabled={readOnly} fieldName="startDate" label="Start Date" margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={7}>
                                        {countries && (
                                            <AutocompleteField
                                                disabled
                                                fieldName="client.country"
                                                label="Country of Birth"
                                                options={countries}
                                                valueKey="code"
                                                labelKey="name"
                                                margin="dense"
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <DatePickerField disabled fieldName="client.birthDate" label="Date of Birth" margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={7}>
                                        <TextField disabled fieldName="client.birthPlace" label="Place of Birth" margin="dense" />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DateTimePickerField
                                            disabled={readOnly}
                                            fieldName="dateOfAccident"
                                            label="Date of Accident"
                                            margin="dense"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            disabled={readOnly}
                                            fieldName="placeOfAccident"
                                            label="Place of Accident"
                                            margin="dense"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        {types && (
                                            <AutocompleteField
                                                disabled={readOnly}
                                                fieldName="type"
                                                label="Case Type"
                                                options={types}
                                                valueKey="code"
                                                labelKey="type"
                                                margin="dense"
                                                getOptionLabel={({ type }) => type}
                                                getOptionSelected={(opt, val) => opt.id === val.id}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField disabled fieldName="client.ssn" label="S.S No." margin="dense" mask={Masks.ssn} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {status && (
                                            <AutocompleteField
                                                disabled={readOnly}
                                                fieldName="status"
                                                label="Status"
                                                options={status}
                                                valueKey="code"
                                                labelKey="name"
                                                margin="dense"
                                                getOptionLabel={({ name }) => name}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextareaField disabled={readOnly} fieldName="notes" label="Notes" fullWidth maxLength={500} />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <TextField disabled fieldName="client.referredBy" label="Referred By" margin="dense" />
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {readOnly && (
                        <Grid container spacing={1} className={classes.buttons}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="default"
                                    startIcon={<BackspaceIcon />}
                                    className={classes.button}
                                    onClick={rest.onCancel}
                                >
                                    Back
                                </Button>
                                {permissions.personalInfo.actions.edit && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        className={classes.button}
                                        onClick={onEdit}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </FormikForm>
            )}
            <DataSearchDialog {...searchClientDialogProps} />
        </>
    );
}

Form.propTypes = {
    fileCase: PropTypes.shape(EntityUtils.getEntityTypes(FileCaseEntity)),
    resources: PropTypes.shape({
        countries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
            }),
        ),
        types: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                type: PropTypes.string,
            }),
        ),
        status: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            }),
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
    prefix: PropTypes.string.isRequired,
    consecutive: PropTypes.number.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

Form.defaultProps = {
    fileCase: EntityUtils.getEntityDefaults(FileCaseEntity),
    resources: {
        countries: [],
        types: [],
        status: [],
    },
    onEdit: () => {},
};

export default Form;
